import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { TAbrandUserCategories } from "../services/userAPI"
import { CheckIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid"

interface SubCategory {
  name?: string
  category?: string
  count: number
}

interface Category {
  main_category: string
  sub_categories: SubCategory[]
}

interface EditCategoryProps {
  category: string
  setCategory: React.Dispatch<React.SetStateAction<string>>
  subCategory: string[]
  setSubCategory: React.Dispatch<React.SetStateAction<string[]>>
  onClose: () => void
  handleSave: () => void
}

const EditCategoryModal: React.FC<EditCategoryProps> = ({
  category,
  setCategory,
  subCategory,
  setSubCategory,
  onClose,
  handleSave,
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [categories, setCategories] = useState<Category[]>([])

  useEffect(() => {
    fetchUserCategories()
  }, [])

  const fetchUserCategories = async () => {
    const response = await TAbrandUserCategories()
    if (response && response.data) {
      setCategories(response.data)
    }
  }

  const handleCategorySelect = (mainCategory: string, subName: string) => {
    setCategory(mainCategory)
    setSubCategory((prev) => {
      if (prev.includes(subName)) {
        return prev.filter((cat) => cat !== subName)
      }
      return [...prev, subName]
    })
  }

  const handleMainCategorySelect = (mainCategory: string) => {
    const categoryItem = categories.find((cat) => cat.main_category === mainCategory)
    if (!categoryItem) {
      return
    }

    const allSubCategories = categoryItem.sub_categories.map(
      (sub) => sub.name || sub.category || "",
    )
    const allSelected = allSubCategories.every((sub) => sub && subCategory.includes(sub))

    if (allSelected) {
      setSubCategory((prev) => prev.filter((cat) => !allSubCategories.includes(cat)))
    } else {
      setCategory(mainCategory)
      setSubCategory((prev) => {
        const newCategories = [...prev]
        allSubCategories.forEach((sub) => {
          if (sub && !newCategories.includes(sub)) {
            newCategories.push(sub)
          }
        })
        return newCategories
      })
    }
  }

  const isSelected = (subCategoryName: string) => subCategory.includes(subCategoryName)

  const isMainCategoryFullySelected = (mainCategory: string) => {
    const categoryItem = categories.find((cat) => cat.main_category === mainCategory)
    if (!categoryItem || categoryItem.sub_categories.length === 0) {
      return false
    }

    const allSubCategories = categoryItem.sub_categories.map(
      (sub) => sub.name || sub.category || "",
    )
    return allSubCategories.every((sub) => sub && subCategory.includes(sub))
  }

  const filteredCategories = categories
    .map((categoryItem) => {
      const filteredSubs = categoryItem.sub_categories.filter((sub) => {
        const subName = sub.name || sub.category || ""
        return subName.toLowerCase().includes(searchTerm.toLowerCase())
      })
      return { ...categoryItem, sub_categories: filteredSubs }
    })
    .filter((categoryItem) => categoryItem.sub_categories.length > 0)

  return (
    <div className="fixed right-0 top-0 h-full z-50">
      <div className="bg-white h-full w-96 shadow-lg flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold">Kategoriyi Düzenle</h2>
              <p className="text-sm opacity-90 mt-1">Kategori Seçimi</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-y-auto">
          {/* Search Input */}
          <div className="relative w-full mb-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Kategori Ara"
              className="w-full pl-12 pr-4 py-3 text-base bg-gray-100 rounded-2xl border-none focus:ring-0 focus:outline-none"
            />
          </div>

          {/* Categories List */}
          <div className="space-y-3">
            {filteredCategories.length === 0 ? (
              <div className="px-4 py-3 text-base text-gray-500 text-center">
                Kategori Bulunamadı
              </div>
            ) : (
              filteredCategories.map((categoryItem) => (
                <div key={categoryItem.main_category} className="space-y-2">
                  <h3
                    className="font-semibold text-gray-800 text-base cursor-pointer hover:text-blue-600 flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                    onClick={() => handleMainCategorySelect(categoryItem.main_category)}
                  >
                    {categoryItem.main_category}
                    {isMainCategoryFullySelected(categoryItem.main_category) && (
                      <CheckIcon className="w-5 h-5 text-blue-600" />
                    )}
                  </h3>
                  <div className="space-y-1 ml-4">
                    {categoryItem.sub_categories.map((subCategory) => {
                      const subName = subCategory.name || subCategory.category || ""
                      return (
                        <div
                          key={subName}
                          className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleCategorySelect(categoryItem.main_category, subName)}
                        >
                          <span className="text-base text-gray-700">{subName || "Kategori"}</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500">({subCategory.count})</span>
                            {isSelected(subName) && <CheckIcon className="w-5 h-5 text-blue-600" />}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {subCategory.length > 0
                ? `${subCategory.length} kategori seçili`
                : "Kategori seçilmedi"}
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={() => {
                  handleSave()
                  onClose()
                }}
                disabled={!category}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditCategoryModal
